/**
 * DV-WS-002: EventFeed Component
 *
 * Live display of incoming WebSocket events with pretty-printed payloads.
 * Mirrors execution portal event handling and cache invalidation patterns.
 */

import React from 'react';
import { formatDistanceToNow } from 'date-fns';
import {
  Activity,
  Clock,
  Hash
} from 'lucide-react';
import { EVENT_TYPE_DISPLAY_CONFIG } from '@/types/execution-events';

// WebSocket event data structures (incoming events)
export interface WebSocketEvent {
  event_type: string;
  stage?: string;
  execution_id: string;
  timestamp: string;
  payload?: any;
  correlation_id?: string;
  workflow_run_id?: string;
  attempt_number?: number;
}

export interface EventFeedProps {
  events: WebSocketEvent[];
  maxEvents?: number;
  showPayloads?: boolean;
  className?: string;
  'data-testid'?: string;
}

// Truncate large payloads for readability
const truncatePayload = (payload: any, maxLength = 200): string => {
  if (!payload) return '';

  try {
    const str = typeof payload === 'string' ? payload : JSON.stringify(payload, null, 2);
    if (str.length <= maxLength) return str;

    return str.substring(0, maxLength) + '...';
  } catch {
    return '[Unable to display payload]';
  }
};

// Safe field extraction avoiding secrets
const extractSafeFields = (payload: any): Record<string, any> => {
  if (!payload || typeof payload !== 'object') return {};

  const safe: Record<string, any> = {};
  const safeKeys = [
    'stage', 'status', 'attempt', 'iteration', 'score', 'decision',
    'event_type', 'execution_id', 'stage_name', 'iteration_number',
    'qc_result', 'billing_amount', 'artifact_count', 'duration_ms'
  ];

  safeKeys.forEach(key => {
    if (key in payload && payload[key] !== undefined) {
      safe[key] = payload[key];
    }
  });

  return safe;
};

// Get icon for event type
const getEventIcon = (eventType: string) => {
  const config = EVENT_TYPE_DISPLAY_CONFIG[eventType as keyof typeof EVENT_TYPE_DISPLAY_CONFIG];

  if (config) {
    return config.icon;
  }

  // Fallback icons based on patterns
  if (eventType.includes('started')) return '▶️';
  if (eventType.includes('completed')) return '✅';
  if (eventType.includes('failed') || eventType.includes('error')) return '❌';
  if (eventType.includes('warning')) return '⚠️';
  if (eventType.includes('billing')) return '💰';
  if (eventType.includes('content') || eventType.includes('ready')) return '📦';

  return '📡'; // Default WebSocket icon
};

// Get severity color
const getSeverityColor = (eventType: string): string => {
  const config = EVENT_TYPE_DISPLAY_CONFIG[eventType as keyof typeof EVENT_TYPE_DISPLAY_CONFIG];

  if (config) {
    switch (config.severity) {
      case 'success': return 'var(--accent)';
      case 'error': return 'var(--danger)';
      case 'warning': return 'var(--warning)';
      case 'info':
      default: return 'var(--fg-primary)';
    }
  }

  // Fallback based on naming patterns
  if (eventType.includes('completed') || eventType.includes('success')) return 'var(--accent)';
  if (eventType.includes('failed') || eventType.includes('error')) return 'var(--danger)';
  if (eventType.includes('warning')) return 'var(--warning)';

  return 'var(--fg-primary)';
};

export const EventFeed: React.FC<EventFeedProps> = ({
  events,
  maxEvents = 50,
  showPayloads = true,
  className = '',
  'data-testid': testId = 'event-feed'
}) => {
  // Show most recent events first, limited to maxEvents
  const displayEvents = events.slice(-maxEvents).reverse();

  if (displayEvents.length === 0) {
    return (
      <div
        className={`border rounded-lg p-6 text-center ${className}`}
        style={{
          backgroundColor: 'var(--bg-surface)',
          borderColor: 'var(--border-default)',
        }}
        data-testid={testId}
      >
        <Activity
          className="w-8 h-8 mx-auto mb-2"
          style={{ color: 'var(--fg-muted)' }}
        />
        <p
          className="text-sm"
          style={{ color: 'var(--fg-muted)' }}
        >
          No events received yet. Connect WebSocket to see live events.
        </p>
      </div>
    );
  }

  return (
    <div
      className={`border rounded-lg overflow-hidden ${className}`}
      style={{
        backgroundColor: 'var(--bg-surface)',
        borderColor: 'var(--border-default)',
      }}
      data-testid={testId}
    >
      {/* Header */}
      <div
        className="px-4 py-3 border-b"
        style={{
          backgroundColor: 'var(--bg-elevated)',
          borderColor: 'var(--border-subtle)',
        }}
      >
        <h3
          className="text-sm font-mono uppercase tracking-wider"
          style={{
            fontFamily: 'var(--font-family-mono)',
            color: 'var(--accent)',
          }}
        >
          Live Event Feed ({displayEvents.length})
        </h3>
      </div>

      {/* Events List */}
      <div
        className="max-h-96 overflow-y-auto"
        style={{ backgroundColor: 'var(--bg-app)' }}
      >
        <div className="space-y-0">
          {displayEvents.map((event, index) => {
            const safeFields = extractSafeFields(event.payload);
            const hasPayload = Object.keys(safeFields).length > 0;
            const eventColor = getSeverityColor(event.event_type);

            return (
              <div
                key={`${event.timestamp}-${index}`}
                className="px-4 py-3 border-b last:border-b-0"
                style={{ borderColor: 'var(--border-subtle)' }}
                data-testid={`${testId}-event`}
              >
                {/* Event Header */}
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <span className="text-lg flex-shrink-0">
                      {getEventIcon(event.event_type)}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div
                        className="text-sm font-medium truncate"
                        style={{ color: eventColor }}
                      >
                        {event.event_type}
                      </div>
                      {event.stage && (
                        <div
                          className="text-xs"
                          style={{ color: 'var(--fg-secondary)' }}
                        >
                          Stage: {event.stage}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Timestamp */}
                  <div
                    className="text-xs flex items-center gap-1 flex-shrink-0"
                    style={{ color: 'var(--fg-muted)' }}
                  >
                    <Clock className="w-3 h-3" />
                    {formatDistanceToNow(new Date(event.timestamp), { addSuffix: true })}
                  </div>
                </div>

                {/* Event Metadata */}
                <div className="flex flex-wrap gap-4 text-xs mb-2">
                  {event.execution_id && (
                    <div
                      style={{ color: 'var(--fg-secondary)' }}
                      className="font-mono"
                    >
                      Execution: {event.execution_id.substring(0, 8)}...
                    </div>
                  )}

                  {event.correlation_id && (
                    <div
                      style={{ color: 'var(--fg-secondary)' }}
                      className="flex items-center gap-1 font-mono"
                    >
                      <Hash className="w-3 h-3" />
                      {event.correlation_id.substring(0, 12)}...
                    </div>
                  )}

                  {event.attempt_number && (
                    <div
                      style={{ color: 'var(--fg-secondary)' }}
                    >
                      Attempt: {event.attempt_number}
                    </div>
                  )}
                </div>

                {/* Payload Display */}
                {showPayloads && hasPayload && (
                  <div
                    className="mt-2 p-2 rounded text-xs font-mono"
                    style={{
                      backgroundColor: 'var(--bg-elevated)',
                      borderColor: 'var(--border-subtle)',
                      border: '1px solid',
                    }}
                  >
                    <div
                      className="text-xs uppercase tracking-wider mb-1"
                      style={{ color: 'var(--fg-muted)' }}
                    >
                      Payload:
                    </div>
                    <pre
                      className="whitespace-pre-wrap text-xs"
                      style={{
                        color: 'var(--fg-primary)',
                        fontFamily: 'var(--font-family-mono)',
                      }}
                    >
                      {truncatePayload(safeFields, 300)}
                    </pre>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer with event count limit indicator */}
      {events.length > maxEvents && (
        <div
          className="px-4 py-2 border-t text-center"
          style={{
            backgroundColor: 'var(--bg-elevated)',
            borderColor: 'var(--border-subtle)',
          }}
        >
          <p
            className="text-xs"
            style={{ color: 'var(--fg-muted)' }}
          >
            Showing last {maxEvents} of {events.length} events
          </p>
        </div>
      )}
    </div>
  );
};